import _ from 'lodash';
import { Observable } from 'rxjs';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';
import { replace, push } from 'react-router-redux';

import BudgetApi from 'entities/budget/api';
import CauseApi from 'entities/cause/api';
import CampaignApi from 'entities/campaign/api';
import LocationsApi from 'entities/location/api';
import DonationMatchingRuleApi from 'entities/donationMatchingRule/api';
import DonationMatchingRule from 'entities/donationMatchingRule/model';
import { selectDonationMatchingRuleById } from 'entities/donationMatchingRule/selectors';
import { selectUserLocations } from 'entities/user/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { selectBudgets } from 'entities/budget/selectors';
import { selectCauses } from 'entities/cause/selectors';
import { selectCampaigns } from 'entities/campaign/selectors';

import {
  initSuccessAction,
  initFailedAction,
  saveSuccessAction,
  saveFailedAction,
  removeSuccessAction,
  removeFailedAction,
} from './actions';
import { FORM_IDENTIFIER, INIT, SAVE, REMOVE } from './constants';

const init = (action$, store) =>
  action$.ofType(INIT).mergeMap(({ id, budgetId }) => {
    const state = store.getState();
    const actions = [BudgetApi.fetchAll()];

    const causes = selectCauses(state);
    if (causes.size === 0) {
      actions.push(CauseApi.fetchAll());
    }

    const campaigns = selectCampaigns(state);
    if (campaigns.size === 0) {
      actions.push(CampaignApi.fetchAll());
    }

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationsApi.fetchAll());
    }

    if (id === 'new') {
      const budgets = selectBudgets(state);
      const budget = budgets.get(budgetId);

      actions.push(
        Observable.of(
          initialize(
            FORM_IDENTIFIER,
            new DonationMatchingRule({
              budget: budgetId,
              currencyCode: budget?.get('currencyCode') || '',
              paymentMethods: ['DeedCredit'],
            }).toForm()
          )
        )
      );
    } else {
      const donationMatchingRule = selectDonationMatchingRuleById(state, id);
      if (donationMatchingRule) {
        actions.push(Observable.of(initialize(FORM_IDENTIFIER, donationMatchingRule.toForm())));
      } else {
        actions.push(
          DonationMatchingRuleApi.fetch(id)
            .map((action) => [
              action,
              initialize(FORM_IDENTIFIER, action.donationMatchingRule?.toForm()),
            ])
            .catch((e) => Observable.of(initFailedAction(e), replace('/notFound')))
        );
      }
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$) =>
  action$.ofType(SAVE).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.from(
        payload.data.id
          ? DonationMatchingRuleApi.update(payload.data.id, payload.data)
          : DonationMatchingRuleApi.create(payload.data)
      )
        .mergeMap((action) => [
          action,
          saveSuccessAction(),
          stopSubmit(FORM_IDENTIFIER),
          initialize(FORM_IDENTIFIER, action.donationMatchingRule.toJS()),
          push('/donations/rules'),
          showMessageAction(payload.data.id ? 'Changes Saved!' : 'Donation Matching Rule Created!'),
        ])
        .catch((e) =>
          Observable.of(
            saveFailedAction(e, 'saveFailed'),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('An error occurred during saving, please try again.')
          )
        )
    )
  );

const remove = (action$) =>
  action$.ofType(REMOVE).mergeMap((payload) =>
    Observable.from(DonationMatchingRuleApi.delete(payload.donationMatchingRuleId))
      .mergeMap((action) => [
        action,
        removeSuccessAction(),
        push('/donations/rules'),
        showMessageAction('Donation Matching Rule Removed!'),
      ])
      .catch((e) =>
        Observable.of(
          removeFailedAction(e, 'removeFailed'),
          stopSubmit(FORM_IDENTIFIER),
          showErrorAction('An error occurred during saving, please try again.')
        )
      )
  );

export default [init, save, remove];
